import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import type { SocketStatus } from '../../gql/socket';

// Surfaces the providerHealthChanged subscription's WebSocket status so the operator
// always knows whether the providers board is actually live, degraded, or reconnecting.
//
// 2026-06-06: now also reads `lastBeatAt` (the most recent server-driven heartbeat) so a
// silently-stalled subscription pipeline — TCP socket reporting healthy but no events
// arriving — surfaces as a STALE state distinct from the connection-level statuses. And
// the pill is now a button: clicking it calls `onReconnect` to forcibly cycle the
// connection. Useful when the socket reports 'open' but isn't actually delivering data.

const SOCKET_STATUS_LABEL: Record<SocketStatus, string> = {
  open: 'Live',
  connecting: 'Connecting…',
  errored: 'Reconnecting',
  closed: 'Offline',
};

/**
 * Threshold beyond which we consider the heartbeat stale even if the socket is open.
 * Heartbeat ticks at ~5s server-side; this allows two missed ticks + a 2s grace window
 * before flipping the operator-visible label.
 */
const STALE_MS = 12_000;

export interface LiveConnectionIndicatorProps {
  status: SocketStatus;
  /**
   * Timestamp of the most recent server heartbeat the SPA observed. When `null` the
   * indicator falls back to socket-status only (the heartbeat subscription may not yet
   * be wired upstream, e.g. in older tests). When set, an open socket with a stale beat
   * is rendered amber as "Stale".
   */
  lastBeatAt?: Date | null;
  /**
   * Click handler. The pill is rendered as a button; operators click to forcibly cycle
   * the live subscription connection. A no-op `() => {}` is fine.
   */
  onReconnect: () => void;
  /**
   * 2026-06-08: also fired on click. Lets the pill double as the page's Refresh
   * button — operators get both a connection-cycle AND a data refetch in one tap,
   * removing the need for a separate Refresh action next to it. Optional so callers
   * that only want the connection-cycle still work.
   */
  onRefresh?: () => void;
  /**
   * Whether the parent's data refetch is currently in flight. Spins the inline
   * refresh icon on the right of the pill so the click feels responsive.
   */
  refreshing?: boolean;
}

export function LiveConnectionIndicator({
  status,
  lastBeatAt = null,
  onReconnect,
  onRefresh,
  refreshing = false,
}: LiveConnectionIndicatorProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Tick a 1Hz timer so the staleness check re-evaluates without depending on new heartbeat
  // arrivals (the whole point: we WANT to flip even when no new data arrives).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const beatAgeMs = lastBeatAt ? now - lastBeatAt.getTime() : null;
  const isStale = status === 'open' && beatAgeMs != null && beatAgeMs > STALE_MS;
  const effectiveStatus: SocketStatus | 'stale' = isStale ? 'stale' : status;

  const label =
    effectiveStatus === 'stale'
      ? 'Stale · click to reconnect'
      : SOCKET_STATUS_LABEL[status];

  const tone: 'ok' | 'warn' | 'danger' =
    effectiveStatus === 'open'
      ? 'ok'
      : effectiveStatus === 'closed' || effectiveStatus === 'errored'
        ? 'danger'
        : 'warn';

  const dotClass =
    tone === 'ok'
      ? isDark
        ? 'bg-emerald-400'
        : 'bg-emerald-500'
      : tone === 'warn'
        ? isDark
          ? 'bg-amber-400'
          : 'bg-amber-500'
        : isDark
          ? 'bg-rose-400'
          : 'bg-rose-500';

  const textClass =
    tone === 'ok'
      ? isDark
        ? 'text-emerald-300'
        : 'text-emerald-700'
      : tone === 'warn'
        ? isDark
          ? 'text-amber-300'
          : 'text-amber-700'
        : isDark
          ? 'text-rose-300'
          : 'text-rose-700';

  const ringClass =
    tone === 'ok'
      ? isDark
        ? 'border-emerald-400/30 bg-emerald-400/[0.08]'
        : 'border-emerald-200 bg-emerald-50'
      : tone === 'warn'
        ? isDark
          ? 'border-amber-400/30 bg-amber-400/[0.08]'
          : 'border-amber-200 bg-amber-50'
        : isDark
          ? 'border-rose-400/30 bg-rose-400/[0.08]'
          : 'border-rose-200 bg-rose-50';

  const tooltip =
    effectiveStatus === 'stale'
      ? `No server heartbeat for ${Math.round((beatAgeMs ?? 0) / 1000)}s — click to reconnect.`
      : effectiveStatus === 'open'
        ? lastBeatAt
          ? `Live · last beat ${Math.max(0, Math.round((beatAgeMs ?? 0) / 1000))}s ago · click to reconnect`
          : 'Live · click to reconnect'
        : `WebSocket status: ${status} · click to reconnect`;

  const handleClick = () => {
    onReconnect();
    onRefresh?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="live-connection-indicator"
      data-status={effectiveStatus}
      title={onRefresh ? `${tooltip} + refresh data` : tooltip}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-transform active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${ringClass} ${textClass}`}
    >
      <span className="relative inline-flex h-2 w-2">
        {effectiveStatus === 'open' && (
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full ${dotClass} opacity-60`}
            aria-hidden="true"
          />
        )}
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotClass}`} />
      </span>
      {label}
      {onRefresh && (
        <RefreshCw
          className={`h-3 w-3 opacity-70 ${refreshing ? 'animate-spin' : ''}`}
          aria-hidden="true"
        />
      )}
    </button>
  );
}
